import React, { useState } from 'react';
import { Mail, Phone, MessageCircle, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import MarketingLayout from '../components/marketing/MarketingLayout';
import PageHero from '../components/marketing/PageHero';
import { IMAGES } from '../lib/marketingImages';
import { CONTACT_EMAIL, SUPPORT_WHATSAPP_NUMBER, SUPPORT_WHATSAPP_URL } from '../config';

const TEL = `+${SUPPORT_WHATSAPP_NUMBER}`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });

  const onSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Hello from ${form.name || 'a customer'}`);
    const body = encodeURIComponent(`${form.message}\n\n— ${form.name}\n${form.email}`);
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    toast.success('Opening your email app…');
  };

  return (
    <MarketingLayout>
      <PageHero
        image={IMAGES.livingRoom}
        eyebrow="Contact"
        title="We are one message away."
        subtitle="WhatsApp, a phone call, or a note — pick whatever feels easiest."
      />
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto grid lg:grid-cols-2 gap-12">
        <div className="space-y-4">
          {[
            { icon: Phone, label: 'Call', value: '+265 994 123 079', href: `tel:${TEL}` },
            { icon: MessageCircle, label: 'WhatsApp', value: 'Chat with support', href: SUPPORT_WHATSAPP_URL },
            { icon: Mail, label: 'Email', value: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
            { icon: MapPin, label: 'Home', value: 'Serving households across Malawi', href: null },
          ].map((row) => (
            row.href ? (
            <a
              key={row.label}
              href={row.href}
              target={row.href.startsWith('http') ? '_blank' : undefined}
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 bg-card border border-border rounded-2xl hover:border-primary/40 transition-colors"
            >
              <span className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <row.icon className="w-5 h-5" />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">{row.label}</span>
                <span className="font-semibold">{row.value}</span>
              </span>
            </a>
          ) : (
            <div key={row.label} className="flex items-center gap-4 p-5 bg-card border border-border rounded-2xl">
              <span className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <row.icon className="w-5 h-5" />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">{row.label}</span>
                <span className="font-semibold">{row.value}</span>
              </span>
            </div>
          )
          ))}
        </div>
        <form onSubmit={onSubmit} className="bg-card border border-border rounded-3xl p-6 sm:p-8 space-y-4">
          <h2 className="text-2xl font-bold">Send a note</h2>
          <input
            required
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-4 py-3 rounded-xl bg-input/50 border border-border"
          />
          <input
            required
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-4 py-3 rounded-xl bg-input/50 border border-border"
          />
          <textarea
            required
            rows={5}
            placeholder="How can we help?"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full px-4 py-3 rounded-xl bg-input/50 border border-border"
          />
          <button type="submit" className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-semibold">
            Send
          </button>
        </form>
      </section>
    </MarketingLayout>
  );
}
